import MapOutlinedIcon from "@mui/icons-material/MapOutlined";
import Chip from "@mui/material/Chip";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import { GridColDef } from "@mui/x-data-grid-pro";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { StayRow } from "../models/StayRow";
import { stayCellValue, StayField } from "../utils/stayExport";
import StayTripsPanel from "./StayTripsPanel";
import { I18nGrid } from "@/components/data/I18nGrid";
import { ExportToolbar } from "@/components/grid/ExportToolbar";

interface Props {
  rows: StayRow[];
  loading: boolean;
  fromDate: Date;
  toDate: Date;
  onShowMap: (row: StayRow) => void;
}

const HOURS = new Intl.NumberFormat(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const MS_PER_HOUR = 3_600_000;

export default function StaysGrid({ rows, loading, fromDate, toDate, onShowMap }: Props) {
  const { t } = useTranslation();

  const columns = useMemo((): GridColDef<StayRow>[] => {
    const labels = { ongoing: t("zoneReportPage.ongoing"), zoneUnknown: t("zoneReportPage.zoneUnknown") };
    const value = (field: StayField) => (_: unknown, row: StayRow) => stayCellValue(row, field, labels);
    return [
      {
        field: "map",
        headerName: "",
        width: 56,
        sortable: false,
        filterable: false,
        disableExport: true,
        disableColumnMenu: true,
        align: "center",
        renderCell: ({ row }) => (
          <Tooltip title={t("zoneReportPage.map.show")}>
            <IconButton size="small" color="primary" onClick={() => onShowMap(row)} aria-label={t("zoneReportPage.map.show")}>
              <MapOutlinedIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        ),
      },
      { field: "deviceName", headerName: t("zoneReportPage.columns.device"), flex: 1, minWidth: 160, valueGetter: value("deviceName") },
      { field: "zoneName", headerName: t("zoneReportPage.columns.zone"), flex: 1, minWidth: 160, valueGetter: value("zoneName") },
      { field: "zoneTypes", headerName: t("zoneReportPage.columns.zoneTypes"), flex: 1, minWidth: 140, valueGetter: value("zoneTypes") },
      { field: "entry", type: "dateTime", headerName: t("zoneReportPage.columns.entry"), minWidth: 170, valueGetter: value("entry") },
      { field: "exit", type: "dateTime", headerName: t("zoneReportPage.columns.exit"), minWidth: 170, valueGetter: value("exit") },
      {
        field: "durationHours",
        type: "number",
        headerName: t("zoneReportPage.columns.durationHours"),
        minWidth: 120,
        valueGetter: value("durationHours"),
        // A running stay shows its time so far, in italics; its value (and the export) stays empty.
        renderCell: ({ row }) =>
          row.ongoing ? (
            <Typography component="span" variant="body2" sx={{ fontStyle: "italic" }}>
              {HOURS.format(row.durationMs / MS_PER_HOUR)}
            </Typography>
          ) : (
            HOURS.format(row.durationMs / MS_PER_HOUR)
          ),
      },
      {
        field: "note",
        headerName: t("zoneReportPage.columns.note"),
        minWidth: 160,
        valueGetter: value("note"),
        renderCell: ({ row, value: note }) =>
          note ? <Chip size="small" label={note as string} color={row.ongoing ? "info" : "default"} variant="outlined" /> : null,
      },
    ];
  }, [t, onShowMap]);

  return (
    <I18nGrid
      sx={{ flexGrow: 1 }}
      rows={rows}
      columns={columns}
      loading={loading}
      disableRowSelectionOnClick
      disableColumnFilter
      disableDensitySelector
      initialState={{ sorting: { sortModel: [] } }}
      getDetailPanelHeight={() => "auto"}
      getDetailPanelContent={({ row }) => <StayTripsPanel row={row as StayRow} />}
      slots={{ toolbar: ExportToolbar }}
      slotProps={{
        toolbar: {
          report: { title: t("zoneReportPage.title"), period: { from: fromDate, to: toDate } },
          fractionDigits: { durationHours: 2 },
          exportForcedFields: ["deviceName", "zoneName", "zoneTypes", "entry", "exit", "durationHours", "note"],
          exportDisabled: loading,
          hideFilter: true,
          hideDensity: true,
          hideQuickFilter: false,
        },
      }}
    />
  );
}
